import { useState, useEffect } from "react";
import { userApi } from "../api/user";
import { UserProfile, Recipe } from "../types";

interface UseUserProfileReturn {
  userData: UserProfile | null;
  recipes: Recipe[];
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook for fetching and managing user profile data.
 *
 * @param userId - The user ID to fetch profile for
 * @returns Object containing user data, recipes, loading state, and error state
 */
export const useUserProfile = (userId?: string): UseUserProfileReturn => {
  const [userData, setUserData] = useState<UserProfile | null>(null);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!userId) {
        setError("User ID is required");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await userApi.getUserProfile(userId);
        setUserData(response.user);
        setRecipes(response.recipes);
        setError(null);
      } catch (err) {
        setError("Failed to load user profile. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [userId]);

  return {
    userData,
    recipes,
    loading,
    error,
  };
};
